// Maps a route path → human page label + default situational mode, so every page reports a
// sensible "where am I" even when it contributes no richer view. Harvested from App.tsx routes.
// Pages that need a richer mode/view override it via useRegisterUIContext.
import type { UIMode } from '@/redux/slices/uiContextSlice';

interface RouteMeta { label: string; mode: UIMode; }

/** Exact-path matches (checked first). */
const ROUTE_LABELS: Record<string, RouteMeta> = {
  '/chat':                    { label: 'AI Chat',          mode: 'chat' },
  '/reports':                 { label: 'Reports',          mode: 'generic' },
  '/weather':                 { label: 'Weather',          mode: 'generic' },
  '/marketing':               { label: 'Marketing',        mode: 'generic' },
  '/settings/tenant':         { label: 'Tenant Settings',  mode: 'settings' },
  '/profile':                 { label: 'Profile',          mode: 'generic' },
  '/admin/lifecycle':         { label: 'Tenant Lifecycle', mode: 'generic' },
  '/admin/roles':             { label: 'Roles',            mode: 'generic' },
  '/admin/email-templates':   { label: 'Email Templates',  mode: 'generic' },
  '/platform/tenants':        { label: 'Tenants',          mode: 'generic' },
  '/platform/users':          { label: 'Users',            mode: 'generic' },
  '/platform/plans':          { label: 'Plans',            mode: 'generic' },
  '/platform/configurations': { label: 'Configurations',   mode: 'settings' },
  '/platform/agents':         { label: 'AI Agents',        mode: 'generic' },
  '/platform/sam-prompts':    { label: 'SAM Prompts',      mode: 'generic' },
  '/platform/sam-rulebook':   { label: 'SAM Screen Rules', mode: 'generic' },
  '/platform/logs':           { label: 'Logs',             mode: 'generic' },
  '/platform/session-logs':   { label: 'Session Logs',     mode: 'generic' },
  '/platform/usage':          { label: 'Usage',            mode: 'generic' },
  '/platform/ai-billing':     { label: 'AI Billing',       mode: 'generic' },
  '/platform/pricing':        { label: 'Pricing',          mode: 'generic' },
};

/** Prefix fallbacks for parameterized routes (longest prefix wins). */
const PREFIX_LABELS: Array<[string, RouteMeta]> = [
  ['/platform/tenants/', { label: 'Tenant Detail', mode: 'generic' }],
  ['/platform/users/',   { label: 'User Detail',   mode: 'generic' }],
  ['/platform/agents/',  { label: 'Agent Config',  mode: 'generic' }],
];

export function routeMetaFor(pathname: string): RouteMeta {
  if (ROUTE_LABELS[pathname]) return ROUTE_LABELS[pathname];
  const hit = PREFIX_LABELS
    .filter(([p]) => pathname.startsWith(p))
    .sort((a, b) => b[0].length - a[0].length)[0];
  return hit ? hit[1] : { label: 'RoofVox', mode: 'generic' };
}
